import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchData } from "../api/apiService";
import { ACCESS } from "../api/endpointMap";
import { useAuth } from "./AuthContext";
// NOTE: don't use `useEmbedToken()` here — OrgUsersProvider sits ABOVE
// EmbedTokenProvider in the tree (index.js wraps the whole App; the
// embed provider only wraps /embed/* routes). React context inheritance
// flows top-down, so `useEmbedToken()` here would always return the
// createContext defaults. Read the module-level token via
// `getEmbedToken()` from api.js instead — that's set by EmbedRoot at
// iframe mount and is observable from any tree position.
import { getEmbedToken } from "../api/api";

// Org-scoped user-list cache for the user-level-client-assignment work
// (USER_LEVEL_CLIENT_ASSIGNMENT.md §7).
//
// Used by:
//   - <UserAssignmentSelect /> — needs the enabled-user list to render.
//   - List-page filter dropdowns — admin sees one option per enabled user.
//   - Team page — full CRUD consumer; refreshes after every action.
//   - Clients-per-Staff dashboard widget — admin-only; reads
//     `assigned_clients_count` per row.
//
// Why a context (not a hook-per-call): the same user list is needed by
// 5+ surfaces simultaneously. Without a shared cache, each component
// would fire its own `GET /users` on mount — N+1 fetch storm on every
// list-page render. Refresh is manual (`refresh()`) so we re-fetch only
// after mutations that actually change the cached data.
//
// Why lazy-load (not on AuthProvider mount): the context lives inside
// `<AuthProvider>` but the embed/login surfaces don't need org users.
// We fetch on first `ensureLoaded()` call instead of fire-and-forget at
// boot — saves a 401 flash on /login + skips the call entirely for the
// iframe surface (which has no `/users` access via partner-SSO JWT).
//
// `assigned_clients_count` is intentionally NOT trusted long-term — it
// goes stale after every assignment change. Team page calls `refresh()`
// on mount + after every reassign / delete; other consumers should also
// `refresh()` after mutations or accept a brief staleness window.

const OrgUsersContext = createContext({
  users: [],
  total: 0,
  loading: false,
  error: null,
  loaded: false,
  isSingleUserOrg: false,
  ensureLoaded: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
});

// We fetch the full org user list in a single page when possible — typical
// orgs are <50 users and the BE GET /users response carries
// `assigned_clients_count` per row, so paginating would force the dashboard
// widget into multiple round-trips. PAGE_SIZE is intentionally generous;
// the >20-user typeahead branch in <UserAssignmentSelect /> is for
// _rendering_, not for fetching.
const PAGE_SIZE = 200;

// Role + activation filter is now applied server-side via the
// /access/users?assignable_only=true contract. Keeping the FE-side
// constant removed (was: ASSIGNABLE_ROLES = {"user", "tenant_admin"})
// — the BE module is the single source of truth so we don't drift if
// the assignable role set ever changes.

export const OrgUsersProvider = ({ children }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Track an in-flight fetch so concurrent `ensureLoaded()` calls from
  // multiple components mounting at once don't trigger N parallel fetches.
  const inFlightRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Generic access endpoint — server-side role + activation filter
      // via assignable_only=true. Standard {items[], total, page, ...}
      // envelope; no more dual-shape handling.
      const raw = await fetchData(ACCESS.USERS, {
        assignable_only: true,
        page: 1,
        page_size: PAGE_SIZE,
      });
      const body = raw?.data || raw || {};
      const list = Array.isArray(body.items) ? body.items : [];
      setUsers(list);
      setTotal(typeof body.total === "number" ? body.total : list.length);
      setLoaded(true);
    } catch (err) {
      // Common path: a logged-out user navigates to a page that mounts a
      // consumer; the 401 is benign — we just stay unloaded. Don't toast.
      setError(err?.detail || err?.message || "Failed to load users");
      setLoaded(true);
    } finally {
      setLoading(false);
      inFlightRef.current = null;
    }
  }, []);

  const ensureLoaded = useCallback(() => {
    // Skip if neither regular-auth user nor embed-token is present
    // (e.g. /login page before sign-in). Both the dashboard surface
    // and the embed iframe surface have valid auth at this point;
    // the BE /users endpoint accepts either as a bearer token.
    // Read embed token fresh on every invocation — EmbedRoot may have
    // set it AFTER this provider mounted.
    const hasAuth = Boolean(currentUser) || Boolean(getEmbedToken());
    if (!hasAuth) return Promise.resolve();
    if (loaded || loading) {
      return inFlightRef.current || Promise.resolve();
    }
    inFlightRef.current = fetchUsers();
    return inFlightRef.current;
  }, [currentUser, loaded, loading, fetchUsers]);

  const refresh = useCallback(() => {
    const hasAuth = Boolean(currentUser) || Boolean(getEmbedToken());
    if (!hasAuth) return Promise.resolve();
    inFlightRef.current = fetchUsers();
    return inFlightRef.current;
  }, [currentUser, fetchUsers]);

  const value = useMemo(
    () => ({
      users,
      total,
      loading,
      error,
      loaded,
      // Definitive "1 user in the org" gate. Falls open (false) until the
      // first fetch resolves — that's intentional: rendering nothing during
      // loading is correct per spec §7.1, but consumers gate on
      // `loaded && isSingleUserOrg` to know it's a SETTLED single-user
      // verdict vs an in-flight load.
      isSingleUserOrg: loaded && total === 1,
      ensureLoaded,
      refresh,
    }),
    [users, total, loading, error, loaded, ensureLoaded, refresh]
  );

  return (
    <OrgUsersContext.Provider value={value}>
      {children}
    </OrgUsersContext.Provider>
  );
};

export const useOrgUsers = () => useContext(OrgUsersContext);
